import { LoadingButton } from "@mui/lab";
import { Box, Button, TextField, Typography } from "@mui/material";
import { createNotes } from "actions/notes";
import {
  updateNotification,
  getAcknowledgeBy,
  getClosedBy,
} from "actions/notifications";
import { getUnitDetails } from "actions/unit";
import { Modal } from "components";
import {
  renderRelativeTimeWithTooltip,
  renderStopped,
  renderWithDateTooltip,
} from "components/DataTable/renderComponents";
import { DataTable } from "empower-energy-owner-components";
import { isEmpty, isNil } from "lodash";
import { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  fromNow,
  formatDate,
  displayDurationBetweenDates,
} from "utils/datetime";

const styles = () => {
  return {
    "& .contained-button": {
      marginRight: "8px",
    },
    "& .acknowledgeBy": {
      marginLeft: "2px",
    },
  };
};

const NotificationModal = ({ open, onClose, notification, groupBy }) => {
  const dispatch = useDispatch();

  const [message, setMessage] = useState("");
  const [list, setList] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const acknowledgeBy = useSelector(
    (state) => state.notifications?.acknowledgeBy,
  );
  const closedBy = useSelector((state) => state.notifications?.closedBy);

  const notesLoading = useSelector((state) => state.notes?.loading);
  const loading = useSelector((state) => state.notifications?.loading);
  const unitDetails = useSelector((state) => state.unit?.details);

  useEffect(() => {
    if (notification.unit !== unitDetails?.id) {
      dispatch(getUnitDetails(notification.unit));
    }

    if (notification.acknowledged_by) {
      dispatch(getAcknowledgeBy(notification.acknowledged_by));
    }

    if (notification.closed_by) {
      dispatch(getClosedBy(notification.closed_by));
    }
  }, [dispatch, notification]);

  useEffect(() => {
    let rows = groupBy ? notification.items : [notification];
    setList(rows);

    const allIds = rows.map((r) => r.id);
    setRowSelectionModel(allIds);
  }, [notification, groupBy]);

  const columns = [
    {
      field: "set",
      headerName: "Started",
      width: 160,
      renderCell: (params) =>
        renderRelativeTimeWithTooltip(params.value * 1000),
    },
    {
      field: "clear",
      headerName: "Stopped",
      width: 160,
      renderCell: (params) =>
        renderWithDateTooltip(params.value * 1000, renderStopped(params.row)),
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 1,
      renderCell: (params) =>
        params.row.clear
          ? displayDurationBetweenDates(
              params.row.set * 1000,
              params.row.clear * 1000,
            )
          : "",
    },
  ];

  const saveNotes = async () => {
    const data = {
      unit: notification.unit,
      source: "notification",
      message: message,
      source_id: notification.id,
    };
    await dispatch(createNotes(data, "", {}, "notification"));
    onClose(false);
  };

  const onClickAcknowledge = async () => {
    const data = {
      acknowledged: rowSelectionModel,
      message: message,
    };

    await dispatch(updateNotification(data));
    onClose(true);
  };

  const onClickMarkClose = async () => {
    const data = {
      closed: rowSelectionModel,
      message: message,
    };

    await dispatch(updateNotification(data));
    onClose(true);
  };

  console.log("rowSelectionModel", rowSelectionModel);

  return (
    <Box>
      <Modal open={open} onClose={() => onClose(false)} width={700} top={"10%"}>
        <Box sx={styles}>
          <Box className="flex items-center" sx={{ gap: 2 }}>
            <Typography variant="h2">{unitDetails?.serial}</Typography>
            <Typography variant="subtitle1">{notification?.message}</Typography>
          </Box>

          <Box sx={{ maxHeight: "500px" }}>
            <Box my={2}>
              <DataTable
                rows={list}
                columns={columns}
                hideFooter={true}
                checkboxSelection
                rowSelectionModel={rowSelectionModel}
                setRowSelectionModel={setRowSelectionModel}
                columnHeaderHeight={36}
                rowHeight={36}
              />
            </Box>

            <Box>
              {!isNil(notification?.acknowledged) && (
                <Box my={1}>
                  <Typography className="flex">
                    {formatDate(notification.acknowledged * 1000)} (
                    {fromNow(notification.acknowledged * 1000)}) -
                    {!isEmpty(acknowledgeBy) && (
                      <span className="acknowledgeBy">{`${acknowledgeBy.first_name} ${acknowledgeBy.last_name}`}</span>
                    )}
                  </Typography>

                  <Typography>Notification acknowledged</Typography>
                </Box>
              )}
              {!isNil(notification?.closed) && (
                <Box my={1}>
                  <Typography className="flex">
                    {formatDate(notification.closed * 1000)} (
                    {fromNow(notification.closed * 1000)}) -
                    {!isEmpty(closedBy) && (
                      <span className="acknowledgeBy">{`${closedBy.first_name} ${closedBy.last_name}`}</span>
                    )}
                  </Typography>

                  <Typography>Notification closed</Typography>
                </Box>
              )}
            </Box>

            <Box className="flex flex-col align-end">
              <Link
                to={`/units/details/${notification.unit}?tab=History`}
                className="text-link"
              >
                view unit history
              </Link>

              <Box my={1} width={"100%"}>
                <TextField
                  label="Note (Optional)"
                  disabled
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  fullWidth
                  multiline
                ></TextField>
              </Box>

              <Box className="flex" mt={1}>
                {isNil(notification?.acknowledged) && (
                  <LoadingButton
                    onClick={onClickAcknowledge}
                    variant="contained"
                    className="contained-button"
                    loading={loading}
                  >
                    Acknowledge
                  </LoadingButton>
                )}
                {!isNil(notification?.acknowledged) && (
                  <LoadingButton
                    onClick={saveNotes}
                    variant="contained"
                    className="contained-button"
                    loading={notesLoading}
                  >
                    Save Note
                  </LoadingButton>
                )}
                {!isNil(notification?.acknowledged) &&
                  isNil(notification?.closed) && (
                    <LoadingButton
                      variant="contained"
                      className="contained-button"
                      loading={loading}
                      onClick={onClickMarkClose}
                    >
                      Mark as Closed
                    </LoadingButton>
                  )}
                <Button onClick={() => onClose(false)} variant="outlined">
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default memo(NotificationModal);
